import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'

type Props = {
  children: React.ReactNode
  lines: number
  showMoreText?: string
  showLessText?: string
  onClick?: () => void
  isOnCoursePage?: boolean
  isDisplayWholetText?: boolean
}

const ReadMore = ({
  children,
  lines,
  onClick,
  showMoreText = 'more',
  showLessText = 'show less',
  isOnCoursePage,
  isDisplayWholetText,
}: Props) => {
  const [isWholeTextVisible, setIsWholeTextVisible] = useState(true)
  const [hasMoreTextLinesThanLimit, setHasMoreTextLinesThanLimit] = useState(
    true
  )

  const textContainerRef = useRef<HTMLDivElement>(null)

  const handleOnClick = () => {
    if (!isOnCoursePage) {
      if (!hasMoreTextLinesThanLimit) return
      if (onClick) onClick()
      setIsWholeTextVisible((currentState) => !currentState)
    }
  }

  const handleCollapseText = () => {
    const textLineHeight = window
      .getComputedStyle(textContainerRef.current)
      .getPropertyValue('line-height')
    textContainerRef.current.style.setProperty(
      'height',
      `${parseInt(textLineHeight) * lines}px`
    )
  }

  const handleExpandText = () =>
    textContainerRef.current.style.setProperty('height', 'auto')

  useEffect(() => {
    isDisplayWholetText ? handleExpandText() : handleCollapseText()
  }, [isDisplayWholetText])

  useEffect(() => {
    !isWholeTextVisible && hasMoreTextLinesThanLimit
      ? handleCollapseText()
      : handleExpandText()
  }, [isWholeTextVisible, hasMoreTextLinesThanLimit])

  // Checks if number of lines are greater than the limit (`line` prop)
  useEffect(() => {
    const textLineHeight = window
      .getComputedStyle(textContainerRef.current)
      .getPropertyValue('line-height')
    const isContainerHeightGreaterThanContent =
      textContainerRef.current.offsetHeight > parseInt(textLineHeight) * lines

    setHasMoreTextLinesThanLimit(isContainerHeightGreaterThanContent)
    setIsWholeTextVisible(!isContainerHeightGreaterThanContent)
  }, [])

  return (
    <div
      onClick={handleOnClick}
      className={classNames('relative flex flex-col items-start', {
        'cursor-pointer': hasMoreTextLinesThanLimit,
      })}
    >
      <div className="overflow-hidden" ref={textContainerRef}>
        {children}
      </div>
      {hasMoreTextLinesThanLimit && !isOnCoursePage && (
        <p
          className={classNames('hover:underline text-orange', {
            'absolute bottom-0 right-0 w-24 text-right': !isWholeTextVisible,
          })}
          style={{
            background:
              !isWholeTextVisible &&
              'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 50%)',
          }}
        >
          ({isWholeTextVisible ? showLessText : showMoreText})
        </p>
      )}
    </div>
  )
}

export default ReadMore
